// ================================================
// CHIEF OF STAFF COMMAND SUITE - INTERCONNECTED DATA
// ================================================
// All data is realistic and interconnected - decisions reference projects,
// action items reference meetings, stakeholders connect to communications

// ================================================
// TEAM MEMBERS (Referenced across all features)
// ================================================
export const executiveTeam = [
  { id: 'exec-1', name: 'Alexandra Chen', avatar: 'AC', role: 'CEO', title: 'Chief Executive Officer', cognitiveLoad: 72, primaryLane: 'vision', activeTasks: 5, pendingReviews: 3 },
  { id: 'exec-2', name: 'Marcus Williams', avatar: 'MW', role: 'COO', title: 'Chief Operating Officer', cognitiveLoad: 68, primaryLane: 'operations', activeTasks: 12, pendingReviews: 2 },
  { id: 'exec-3', name: 'Sarah Martinez', avatar: 'SM', role: 'East Regional Director', title: 'East Regional Director', cognitiveLoad: 75, primaryLane: 'operations', activeTasks: 14, pendingReviews: 1 },
  { id: 'exec-4', name: 'James Chen', avatar: 'JC', role: 'West Regional Director', title: 'West Regional Director', cognitiveLoad: 88, primaryLane: 'operations', activeTasks: 18, pendingReviews: 4 },
  { id: 'exec-5', name: 'Jessica Park', avatar: 'JP', role: 'Marketing Director', title: 'VP of Marketing', cognitiveLoad: 82, primaryLane: 'marketing', activeTasks: 16, pendingReviews: 2 },
  { id: 'exec-6', name: 'David Park', avatar: 'DP', role: 'Finance Director', title: 'Chief Financial Officer', cognitiveLoad: 58, primaryLane: 'operations', activeTasks: 8, pendingReviews: 0 },
  { id: 'exec-7', name: 'Elena Rodriguez', avatar: 'ER', role: 'South Regional Director', title: 'South Regional Director', cognitiveLoad: 71, primaryLane: 'operations', activeTasks: 11, pendingReviews: 1 },
  { id: 'exec-8', name: 'Marcus Rivera', avatar: 'MR', role: 'Engineering Lead', title: 'VP of Engineering', cognitiveLoad: 79, primaryLane: 'execution', activeTasks: 15, pendingReviews: 3 },
  { id: 'sf-1', name: 'Jordan Lee', avatar: 'JL', role: 'Soul Food Director', title: 'Soul Food - Creative Director', cognitiveLoad: 62, primaryLane: 'creative', activeTasks: 8, pendingReviews: 1 },
  { id: 'sf-2', name: 'Maya Johnson', avatar: 'MJ', role: 'Soul Food Partnerships', title: 'Soul Food - Partnerships Lead', cognitiveLoad: 68, primaryLane: 'relationships', activeTasks: 6, pendingReviews: 2 },
  { id: 'sf-3', name: 'Tyler Washington', avatar: 'TW', role: 'Soul Food Operations', title: 'Soul Food - Operations Manager', cognitiveLoad: 65, primaryLane: 'operations', activeTasks: 9, pendingReviews: 0 }
];

// ================================================
// EXECUTIVE REVIEWS (Critical Decisions Pending)
// ================================================
export const executiveReviews = [
  {
    id: 'rev-1',
    title: 'Q2 National Marketing Campaign Budget',
    project: 'Q2 Marketing Initiative',
    submitter: executiveTeam[4], // Jessica Park
    submittedAt: new Date(Date.now() - 42 * 60 * 60 * 1000), // 42 hours ago
    hoursPending: 42,
    slaStatus: 'warning',
    type: 'Budget Approval',
    summary: 'Request for $850K budget allocation for regional media spend across 12 markets',
    decisionNeeded: 'Approve budget allocation and media mix strategy'
  },
  {
    id: 'rev-2',
    title: 'Southeast Regional Expansion Plan',
    project: 'Regional Expansion',
    submitter: executiveTeam[6], // Elena Rodriguez
    submittedAt: new Date(Date.now() - 28 * 60 * 60 * 1000),
    hoursPending: 28,
    slaStatus: 'warning',
    type: 'Strategic Initiative',
    summary: 'Three-phase expansion into Atlanta, Charlotte, and Miami markets',
    decisionNeeded: 'Approve expansion timeline and resource allocation'
  },
  {
    id: 'rev-3',
    title: 'Soul Food Nike Partnership Terms',
    project: 'Soul Food Partnerships',
    submitter: executiveTeam[9], // Maya Johnson
    submittedAt: new Date(Date.now() - 18 * 60 * 60 * 1000),
    hoursPending: 18,
    slaStatus: 'on_track',
    type: 'Partnership Contract',
    summary: 'Exclusive collaboration terms for Summer 2026 limited edition collection',
    decisionNeeded: 'Approve term sheet and authorize legal review'
  },
  {
    id: 'rev-4',
    title: 'Engineering Team Restructure',
    project: 'Platform Evolution',
    submitter: executiveTeam[7], // Marcus Rivera
    submittedAt: new Date(Date.now() - 52 * 60 * 60 * 1000),
    hoursPending: 52,
    slaStatus: 'at_risk',
    type: 'Organizational Change',
    summary: 'Proposal to create dedicated Platform and Infrastructure squads',
    decisionNeeded: 'Approve restructure and headcount reallocation'
  },
  {
    id: 'rev-5',
    title: 'West Region Recovery Investment',
    project: 'West Region Turnaround',
    submitter: executiveTeam[3], // James Chen
    submittedAt: new Date(Date.now() - 14 * 60 * 60 * 1000),
    hoursPending: 14,
    slaStatus: 'on_track',
    type: 'Budget Request',
    summary: 'Emergency funding request for contractor support to address velocity gap',
    decisionNeeded: 'Approve $120K contractor budget for Q1'
  }
];

// ================================================
// EXECUTIVE SCHEDULE (Today's Meetings)
// ================================================
export const executiveSchedule = [
  {
    id: 'meet-1',
    title: 'Board Strategy Session',
    time: '9:00 AM',
    duration: '90 min',
    location: 'Executive Boardroom / Zoom',
    type: 'board',
    attendees: ['Alexandra Chen', 'David Park', 'Board Members'],
    prepNote: 'Q1 performance deck ready. Key talking point: Southeast expansion approval.',
    agenda: ['Q1 Performance Review', 'Strategic Initiatives Update', 'Board Questions'],
    checklist: { agendaSent: true, materialsDistributed: true, roomConfirmed: true, executiveBriefed: true }
  },
  {
    id: 'meet-2',
    title: 'Leadership Team Weekly Sync',
    time: '11:00 AM',
    duration: '60 min',
    location: 'Conference Room A',
    type: 'internal',
    attendees: ['All Directors', 'Regional Leads'],
    prepNote: 'West Region recovery plan is main agenda item. James needs support.',
    agenda: ['Regional Updates', 'Cross-Functional Blockers', 'Decision Queue Review'],
    checklist: { agendaSent: true, materialsDistributed: false, roomConfirmed: true, executiveBriefed: false }
  },
  {
    id: 'meet-3',
    title: 'Soul Food Partnership Review',
    time: '2:00 PM',
    duration: '45 min',
    location: 'Video Call',
    type: 'partnership',
    attendees: ['Jordan Lee', 'Maya Johnson', 'Nike Representatives'],
    prepNote: 'Nike team wants to discuss exclusivity terms. Legal has flagged concerns.',
    agenda: ['Term Sheet Review', 'Exclusivity Discussion', 'Timeline Alignment'],
    checklist: { agendaSent: true, materialsDistributed: true, roomConfirmed: true, executiveBriefed: true }
  },
  {
    id: 'meet-4',
    title: 'Executive 1:1 with Marcus Rivera',
    time: '4:00 PM',
    duration: '30 min',
    location: 'CEO Office',
    type: 'one-on-one',
    attendees: ['Marcus Rivera'],
    prepNote: 'Engineering restructure discussion. Marcus has concerns about timeline.',
    agenda: ['Restructure Proposal', 'Platform Evolution Status', 'Team Morale'],
    checklist: { agendaSent: false, materialsDistributed: false, roomConfirmed: true, executiveBriefed: false }
  }
];

// ================================================
// ACTION ITEMS (Cross-meeting commitments)
// ================================================
export const actionItems = [
  // Overdue
  {
    id: 'ai-1',
    description: 'Send updated Q1 financial projections to board',
    assignee: executiveTeam[5], // David Park
    assignedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    status: 'overdue',
    source: 'Board Meeting - Feb 14',
    priority: 'high'
  },
  {
    id: 'ai-2',
    description: 'Finalize West Region contractor candidates',
    assignee: executiveTeam[3], // James Chen
    assignedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    status: 'overdue',
    source: 'Leadership Sync - Feb 12',
    priority: 'critical'
  },
  // Due Today
  {
    id: 'ai-3',
    description: 'Submit marketing campaign creative brief for review',
    assignee: executiveTeam[4], // Jessica Park
    assignedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    dueDate: new Date(),
    status: 'due_today',
    source: 'Marketing Review - Feb 17',
    priority: 'high'
  },
  {
    id: 'ai-4',
    description: 'Confirm Soul Food community event venue',
    assignee: executiveTeam[10], // Tyler Washington
    assignedDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
    dueDate: new Date(),
    status: 'due_today',
    source: 'Soul Food Sync - Feb 16',
    priority: 'medium'
  },
  // Due This Week
  {
    id: 'ai-5',
    description: 'Complete engineering team skill assessment',
    assignee: executiveTeam[7], // Marcus Rivera
    assignedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
    status: 'in_progress',
    source: 'Executive 1:1 - Feb 15',
    priority: 'medium'
  },
  {
    id: 'ai-6',
    description: 'Draft Southeast expansion communication plan',
    assignee: executiveTeam[6], // Elena Rodriguez
    assignedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
    status: 'in_progress',
    source: 'Strategy Session - Feb 17',
    priority: 'high'
  },
  {
    id: 'ai-7',
    description: 'Prepare Nike partnership presentation for board',
    assignee: executiveTeam[9], // Maya Johnson
    assignedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000),
    status: 'in_progress',
    source: 'Partnership Review - Feb 18',
    priority: 'high'
  },
  // Upcoming
  {
    id: 'ai-8',
    description: 'Submit Q2 budget proposals',
    assignee: executiveTeam[1], // Marcus Williams
    assignedDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    status: 'not_started',
    source: 'Finance Review - Feb 19',
    priority: 'medium'
  },
  {
    id: 'ai-9',
    description: 'Complete regional capacity assessment',
    assignee: executiveTeam[2], // Sarah Martinez
    assignedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
    status: 'not_started',
    source: 'Operations Review - Feb 18',
    priority: 'medium'
  }
];

// ================================================
// DECISION LOG (15-20 realistic decisions)
// ================================================
export const decisionLog = [
  {
    id: 'dec-1',
    date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    decision: 'Approved Soul Food Nike partnership to proceed to term sheet negotiation',
    decisionMaker: executiveTeam[0], // Alexandra Chen
    context: 'Partnership Review Meeting',
    lane: 'relationships',
    level: 'L4',
    rationale: 'Strategic alignment with brand expansion goals and positive ROI projections',
    impactTag: 'Strategic',
    followUpActions: ['Legal review of terms', 'Finance to model revenue share']
  },
  {
    id: 'dec-2',
    date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    decision: 'Authorized emergency contractor budget for West Region recovery',
    decisionMaker: executiveTeam[0],
    context: 'Leadership Weekly Sync',
    lane: 'operations',
    level: 'L4',
    rationale: 'West Region velocity at critical level requiring immediate intervention',
    impactTag: 'Operational',
    followUpActions: ['James to submit contractor candidates', 'HR to expedite onboarding']
  },
  {
    id: 'dec-3',
    date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    decision: 'Postponed Q2 marketing campaign launch by 48 hours',
    decisionMaker: executiveTeam[4], // Jessica Park
    context: 'Marketing Team Standup',
    lane: 'marketing',
    level: 'L3',
    rationale: 'Creative assets require additional revision based on brand feedback',
    impactTag: 'Operational',
    followUpActions: ['Creative team to finalize by Thursday', 'Update stakeholder communications']
  },
  {
    id: 'dec-4',
    date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
    decision: 'Approved Southeast regional expansion into three markets',
    decisionMaker: executiveTeam[0],
    context: 'Board Strategy Session',
    lane: 'strategy',
    level: 'L4',
    rationale: 'Market analysis shows strong demand and competitive positioning opportunity',
    impactTag: 'Strategic',
    followUpActions: ['Elena to draft implementation plan', 'Finance to allocate capital']
  },
  {
    id: 'dec-5',
    date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    decision: 'Restructured engineering into Platform and Infrastructure squads',
    decisionMaker: executiveTeam[0],
    context: 'Executive Review',
    lane: 'operations',
    level: 'L4',
    rationale: 'Current structure creating bottlenecks in Platform Evolution delivery',
    impactTag: 'Organizational',
    followUpActions: ['Marcus to communicate to team', 'HR to update org charts']
  },
  {
    id: 'dec-6',
    date: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
    decision: 'Selected Venue A for Soul Food Summer Series kickoff event',
    decisionMaker: executiveTeam[8], // Jordan Lee
    context: 'Soul Food Planning Meeting',
    lane: 'creative',
    level: 'L3',
    rationale: 'Best combination of capacity, location, and brand alignment',
    impactTag: 'Operational',
    followUpActions: ['Tyler to confirm booking', 'Marketing to update promotional materials']
  },
  {
    id: 'dec-7',
    date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    decision: 'Approved new vendor contract for cloud infrastructure',
    decisionMaker: executiveTeam[1], // Marcus Williams
    context: 'Operations Review',
    lane: 'operations',
    level: 'L3',
    rationale: '18% cost reduction with improved SLA terms',
    impactTag: 'Financial',
    followUpActions: ['Legal to finalize contract', 'Engineering to plan migration']
  },
  {
    id: 'dec-8',
    date: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
    decision: 'Declined partnership inquiry from competitor brand',
    decisionMaker: executiveTeam[0],
    context: 'Executive Review',
    lane: 'strategy',
    level: 'L4',
    rationale: 'Potential conflict with existing partnerships and brand positioning',
    impactTag: 'Strategic',
    followUpActions: ['Draft polite decline response', 'Document reasoning for future reference']
  },
  {
    id: 'dec-9',
    date: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000),
    decision: 'Increased East Region Q2 marketing budget by 15%',
    decisionMaker: executiveTeam[5], // David Park
    context: 'Finance Review',
    lane: 'operations',
    level: 'L3',
    rationale: 'Strong Q1 performance justifies increased investment',
    impactTag: 'Financial',
    followUpActions: ['Update budget allocations', 'Sarah to submit revised plans']
  },
  {
    id: 'dec-10',
    date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    decision: 'Approved Soul Food community partnership with local arts foundation',
    decisionMaker: executiveTeam[8], // Jordan Lee
    context: 'Soul Food Leadership Meeting',
    lane: 'culture',
    level: 'L3',
    rationale: 'Aligns with community engagement strategy and brand values',
    impactTag: 'Cultural',
    followUpActions: ['Draft partnership agreement', 'Plan joint announcement']
  },
  {
    id: 'dec-11',
    date: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000),
    decision: 'Implemented new executive review SLA of 48 hours',
    decisionMaker: executiveTeam[0],
    context: 'Operations Excellence Initiative',
    lane: 'operations',
    level: 'L4',
    rationale: 'Previous average review time of 5+ days causing downstream delays',
    impactTag: 'Operational',
    followUpActions: ['Chief of Staff to track compliance', 'Weekly reporting to leadership']
  },
  {
    id: 'dec-12',
    date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
    decision: 'Consolidated three regional reporting systems into unified dashboard',
    decisionMaker: executiveTeam[1], // Marcus Williams
    context: 'Operations Review',
    lane: 'execution',
    level: 'L3',
    rationale: 'Reduce reporting overhead and improve data consistency',
    impactTag: 'Operational',
    followUpActions: ['Engineering to build unified dashboard', 'Training for regional teams']
  },
  {
    id: 'dec-13',
    date: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000),
    decision: 'Approved creative direction for Soul Food Summer Series branding',
    decisionMaker: executiveTeam[8], // Jordan Lee
    context: 'Creative Review',
    lane: 'creative',
    level: 'L3',
    rationale: 'Direction A tested strongest with target demographic in focus groups',
    impactTag: 'Creative',
    followUpActions: ['Finalize brand assets', 'Begin production on marketing materials']
  },
  {
    id: 'dec-14',
    date: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000),
    decision: 'Moved board meeting from March 1 to March 3',
    decisionMaker: executiveTeam[0],
    context: 'Board Coordination',
    lane: 'operations',
    level: 'L4',
    rationale: 'Two board members have conflicts on original date',
    impactTag: 'Operational',
    followUpActions: ['Update all calendars', 'Notify all presenters']
  },
  {
    id: 'dec-15',
    date: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000),
    decision: 'Selected TechVentures as lead investor for Series C discussions',
    decisionMaker: executiveTeam[0],
    context: 'Executive Strategy Session',
    lane: 'strategy',
    level: 'L4',
    rationale: 'Best strategic fit, favorable terms, and strong portfolio network',
    impactTag: 'Strategic',
    followUpActions: ['Schedule introductory meetings', 'Prepare data room']
  }
];

// ================================================
// STAKEHOLDERS (External relationships)
// ================================================
export const stakeholders = [
  {
    id: 'stake-1',
    name: 'Robert Chen',
    organization: 'Board of Directors',
    type: 'Board Member',
    avatar: 'RC',
    lastContact: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    lastTopic: 'Q1 performance review and expansion strategy',
    nextFollowUp: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
    healthScore: 85,
    healthStatus: 'healthy',
    notes: 'Supportive of growth strategy. Wants monthly updates on expansion progress.'
  },
  {
    id: 'stake-2',
    name: 'Patricia Williams',
    organization: 'Board of Directors',
    type: 'Board Member',
    avatar: 'PW',
    lastContact: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
    lastTopic: 'Governance and risk management discussion',
    nextFollowUp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    healthScore: 55,
    healthStatus: 'followup_due',
    notes: 'Prefers formal communication. Concerned about rapid expansion risks.'
  },
  {
    id: 'stake-3',
    name: 'Michael Torres',
    organization: 'Nike',
    type: 'Strategic Partner',
    avatar: 'MT',
    lastContact: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    lastTopic: 'Soul Food collaboration term sheet review',
    nextFollowUp: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000),
    healthScore: 78,
    healthStatus: 'healthy',
    notes: 'Key decision maker on Nike side. Meeting scheduled for Thursday.'
  },
  {
    id: 'stake-4',
    name: 'Jennifer Adams',
    organization: 'TechVentures Capital',
    type: 'Investor',
    avatar: 'JA',
    lastContact: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000),
    lastTopic: 'Series C preliminary discussions',
    nextFollowUp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    healthScore: 42,
    healthStatus: 'needs_attention',
    notes: 'Waiting for our follow-up on data room access. High priority.'
  },
  {
    id: 'stake-5',
    name: 'David Kim',
    organization: 'Acme Corp',
    type: 'Key Client',
    avatar: 'DK',
    lastContact: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    lastTopic: 'Contract renewal discussions',
    nextFollowUp: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    healthScore: 72,
    healthStatus: 'healthy',
    notes: 'Happy with service. Discussing expanded scope for renewal.'
  },
  {
    id: 'stake-6',
    name: 'Lisa Thompson',
    organization: 'City Arts Foundation',
    type: 'Community Partner',
    avatar: 'LT',
    lastContact: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    lastTopic: 'Soul Food community partnership planning',
    nextFollowUp: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
    healthScore: 68,
    healthStatus: 'healthy',
    notes: 'Soul Food relationship. Excited about Summer Series collaboration.'
  },
  {
    id: 'stake-7',
    name: 'James Morrison',
    organization: 'Regional Government',
    type: 'Government',
    avatar: 'JM',
    lastContact: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    lastTopic: 'Southeast expansion regulatory requirements',
    nextFollowUp: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    healthScore: 38,
    healthStatus: 'needs_attention',
    notes: 'Need to schedule follow-up on permit applications. Overdue.'
  },
  {
    id: 'stake-8',
    name: 'Sarah Mitchell',
    organization: 'MediaGroup Inc',
    type: 'Strategic Partner',
    avatar: 'SM',
    lastContact: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
    lastTopic: 'Q2 marketing partnership opportunities',
    nextFollowUp: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    healthScore: 75,
    healthStatus: 'healthy',
    notes: 'Interested in expanded partnership for Q2 campaign.'
  },
  {
    id: 'stake-9',
    name: 'Marcus Johnson',
    organization: 'Community Leaders Council',
    type: 'Community Partner',
    avatar: 'MJ',
    lastContact: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000),
    lastTopic: 'Soul Food community event planning',
    nextFollowUp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    healthScore: 58,
    healthStatus: 'followup_due',
    notes: 'Soul Food relationship. Awaiting confirmation on event participation.'
  },
  {
    id: 'stake-10',
    name: 'Emily Carter',
    organization: 'Innovation Partners',
    type: 'Investor',
    avatar: 'EC',
    lastContact: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
    lastTopic: 'Portfolio company updates',
    nextFollowUp: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    healthScore: 82,
    healthStatus: 'healthy',
    notes: 'Existing investor. Pleased with Q1 progress.'
  }
];

// ================================================
// DELEGATIONS (Executive delegated items)
// ================================================
export const delegations = [
  {
    id: 'del-1',
    description: 'Prepare Q1 board presentation deck',
    assignee: executiveTeam[5], // David Park
    delegatedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    status: 'overdue',
    source: 'Executive 1:1 - Feb 13'
  },
  {
    id: 'del-2',
    description: 'Draft response to investor inquiry',
    assignee: executiveTeam[1], // Marcus Williams
    delegatedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    status: 'overdue',
    source: 'Verbal delegation - Feb 15'
  },
  {
    id: 'del-3',
    description: 'Review and summarize competitor analysis',
    assignee: executiveTeam[4], // Jessica Park
    delegatedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000),
    status: 'in_progress',
    source: 'Strategy Session - Feb 17'
  },
  {
    id: 'del-4',
    description: 'Schedule quarterly all-hands meeting',
    assignee: executiveTeam[1], // Marcus Williams
    delegatedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
    status: 'in_progress',
    source: 'Leadership Sync - Feb 18'
  },
  {
    id: 'del-5',
    description: 'Coordinate regional directors quarterly planning',
    assignee: executiveTeam[2], // Sarah Martinez
    delegatedDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    status: 'in_progress',
    source: 'Operations Review - Feb 16'
  },
  {
    id: 'del-6',
    description: 'Finalize Soul Food event vendor contracts',
    assignee: executiveTeam[10], // Tyler Washington
    delegatedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    dueDate: new Date(),
    status: 'in_progress',
    source: 'Soul Food Sync - Feb 17'
  },
  // Completed this week
  {
    id: 'del-7',
    description: 'Update organizational chart with new structure',
    assignee: executiveTeam[1],
    delegatedDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    completedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    status: 'completed',
    source: 'Executive Review - Feb 14'
  },
  {
    id: 'del-8',
    description: 'Send thank you notes to board members',
    assignee: executiveTeam[0],
    delegatedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    completedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    status: 'completed',
    source: 'Board Meeting - Feb 15'
  }
];

// ================================================
// TIME AUDIT DATA (Executive time allocation)
// ================================================
export const timeAuditData = {
  currentWeek: {
    planned: {
      meetings: 18,
      deepWork: 8,
      oneOnOnes: 4,
      external: 3,
      administrative: 2
    },
    actual: {
      meetings: 22,
      deepWork: 4,
      oneOnOnes: 5,
      external: 4,
      administrative: 5
    },
    byLane: {
      vision: { planned: 4, actual: 2 },
      strategy: { planned: 8, actual: 6 },
      relationships: { planned: 6, actual: 8 },
      operations: { planned: 10, actual: 14 },
      execution: { planned: 4, actual: 8 },
      creative: { planned: 2, actual: 1 },
      marketing: { planned: 3, actual: 2 },
      culture: { planned: 1, actual: 1 }
    }
  },
  executiveEffectivenessScore: 62,
  recommendations: [
    {
      id: 'rec-1',
      text: 'Move weekly vendor sync to a delegate — this is taking 2 hours of L4 executive time weekly',
      impact: 'high',
      timeRecovery: '2 hours/week'
    },
    {
      id: 'rec-2',
      text: 'Protect Thursday mornings for Vision lane thinking — currently 0 hours of Vision work scheduled',
      impact: 'high',
      timeRecovery: '3 hours/week'
    },
    {
      id: 'rec-3',
      text: 'Consolidate 1:1 meetings to two designated days to reduce context switching',
      impact: 'medium',
      timeRecovery: '1 hour/week'
    }
  ],
  weeklyHistory: [
    { week: 'Feb 3', effectivenessScore: 58, visionHours: 1, operationsHours: 16 },
    { week: 'Feb 10', effectivenessScore: 55, visionHours: 0, operationsHours: 18 },
    { week: 'Feb 17', effectivenessScore: 62, visionHours: 2, operationsHours: 14 }
  ]
};

// ================================================
// ORGANIZATION METRICS (For briefing and bridge)
// ================================================
export const organizationMetrics = {
  company: {
    executionRate: 87,
    activeProjects: 24,
    teamMembersAtCapacity: 8,
    burnoutRiskAlerts: 3,
    velocityThisWeek: 156,
    velocityTrend: 'up',
    decisionsThisWeek: 18,
    onTimeDelivery: 82,
    criticalItems: 4,
    topPriority: 'Southeast Regional Expansion'
  },
  soulFood: {
    executionRate: 78,
    activeProjects: 6,
    teamMembersAtCapacity: 2,
    burnoutRiskAlerts: 0,
    velocityThisWeek: 28,
    velocityTrend: 'up',
    decisionsThisWeek: 5,
    onTimeDelivery: 88,
    criticalItems: 1,
    topPriority: 'Nike Partnership Finalization',
    momentumScore: 82,
    upcomingEvents: 3,
    brandHealth: 87
  }
};

// ================================================
// AWARENESS ITEMS (For briefing)
// ================================================
export const awarenessItems = [
  {
    id: 'aware-1',
    text: 'Regional Director flagged a capacity gap in the Southeast Operations team',
    category: 'operations',
    source: 'Elena Rodriguez'
  },
  {
    id: 'aware-2',
    text: 'Marketing campaign launch delayed 48 hours due to creative approval bottleneck',
    category: 'marketing',
    source: 'Jessica Park'
  },
  {
    id: 'aware-3',
    text: 'Soul Food community event on Saturday is confirmed and fully staffed',
    category: 'soulfood',
    source: 'Tyler Washington'
  },
  {
    id: 'aware-4',
    text: 'Engineering team morale survey shows 15% improvement from last quarter',
    category: 'culture',
    source: 'Marcus Rivera'
  },
  {
    id: 'aware-5',
    text: 'West Region velocity showing early signs of recovery after intervention',
    category: 'operations',
    source: 'James Chen'
  }
];

// ================================================
// WINS TO ACKNOWLEDGE (For briefing)
// ================================================
export const winsToAcknowledge = [
  {
    id: 'win-1',
    text: 'Sarah Martinez\'s East Region achieved 100% on-time delivery for the third consecutive week',
    person: 'Sarah Martinez',
    category: 'performance'
  },
  {
    id: 'win-2',
    text: 'Platform Evolution project completed Phase 2 milestone ahead of schedule',
    person: 'Marcus Rivera',
    category: 'milestone'
  },
  {
    id: 'win-3',
    text: 'Soul Food secured partnership with City Arts Foundation for Summer Series',
    person: 'Jordan Lee',
    category: 'partnership'
  }
];

// ================================================
// SYNC ITEMS (Cross-entity items for Bridge)
// ================================================
export const syncItems = [
  {
    id: 'sync-1',
    title: 'Executive participation in Soul Food community event',
    type: 'event',
    date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    entities: ['company', 'soulfood']
  },
  {
    id: 'sync-2',
    title: 'Nike partnership affects both brand strategies',
    type: 'decision',
    entities: ['company', 'soulfood']
  },
  {
    id: 'sync-3',
    title: 'Tyler Washington supporting company operations during Q2 push',
    type: 'resource',
    entities: ['company', 'soulfood']
  }
];

// ================================================
// HELPER FUNCTIONS
// ================================================
export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  });
};

export const formatTime = (date) => {
  return new Date(date).toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
};

export const getRelativeTime = (date) => {
  const now = new Date();
  const diff = now - new Date(date);
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  return 'Just now';
};

export const getDaysUntil = (date) => {
  const now = new Date();
  const target = new Date(date);
  const diff = target - now;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (days < 0) return `${Math.abs(days)} days overdue`;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  return `Due in ${days} days`;
};
